import React from 'react';
import { updateAttributeTransform, updateCodeContainer } from 'js/actions';
import ResourceDetailHeader from 'js/components/ResourceDetailHeader/ResourceDetailHeader';
import { User } from 'new/entities/User';
import ReusableTxDetailActionsContainer from 'js/components/reusableTransforms/cards/ReusableTxDetailActionsContainer';
import DateUtils from 'js/lib/DateUtils';
import { TransformType } from 'new/components/ReusableTransform/types';
import { ReactComponent as ReusableIcon } from 'new/components/icons/fi-rr-recycle.svg';
import { ReactComponent as TransformsIcon } from 'new/components/icons/fi-rr-tool-crop.svg';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { mapStateToProps } from 'js/components/data_maps/cards/stateMapper';
import UIUtils from 'js/lib/UIUtils';
import styles from './ReusableTransformHeader.module.scss';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface ReusableTransformDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  hideActions?: boolean;
  disableEditing: boolean;
  owner: User;
  data: any;
  status: any;
  isEditable: any;
  isDetected: boolean;
  disableEditMessage: any;
  isManaged: any;
  onCloseDrawer?: () => void;
  onEditButtonClicked: () => void;
}

const BoxInfo = ResourceDetailHeader.TopContent.BoxInfo;

type OwnProps = {
  cardData: any;
  readOnly: any;
  isManaged: any;
  owner: any;
  adminAccess: any;
};

export function useGetDataToReusableTransformDetailHeader(data: Array<any>, aclQueryMode: string) {
  return useListOldReduxContainer(
    data,
    mapStateToProps,
    (ownProps: OwnProps) => {
      const { cardData, readOnly, isManaged, owner, adminAccess } = ownProps;

      const name = cardData?.name || '';
      const description = cardData?.description || ' ';

      const createdAt = DateUtils.convertDateStringToFormat(cardData?.created_at);

      return {
        name,
        description,
        createdAt,
        cardData,
        readOnly,
        adminAccess,
        aclQueryMode,
        owner,
        isManaged,
      };
    },
    [aclQueryMode],
  );
}

export const ReusableTransformDetailHeader = (props: ReusableTransformDetailHeaderProps) => {
  const {
    readOnly,
    adminAccess,
    hideActions,
    data,
    isManaged,
    onCloseDrawer,
    onEditButtonClicked,
  } = props;

  const dispatch = useAppDispatch();

  const updateInfo = (id: number, info: {}) => {
    const action =
      data.output_type === TransformType.ATTRIBUTE ? updateAttributeTransform : updateCodeContainer;
    dispatch(action(id, info));
  };

  return (
    <ResourceDetailHeader
      readOnly={readOnly}
      adminAccess={adminAccess}
      data={data}
      updateInfo={updateInfo}
      onCloseDrawer={onCloseDrawer}
    >
      <ResourceDetailHeader.TopContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
          <Stack direction="row" alignItems="center" gap="16px">
            <BoxInfo>
              <TransformsIcon />
              <Typography variant="body3">Transform</Typography>
            </BoxInfo>
            {data.reusable ? (
              <BoxInfo>
                <ReusableIcon />
                <Typography variant="body3">Reusable</Typography>
              </BoxInfo>
            ) : null}
            {data.public ? (
              <div className={styles.infoTile}>
                <Typography variant="body3">Public</Typography>
              </div>
            ) : null}
            {data.output_type ? (
              <div className={styles.infoTile}>
                <Typography variant="body3">{UIUtils.toTitleCase(data.output_type)}</Typography>
              </div>
            ) : null}
            <ChipWithCopy tooltipTitle="Copy Transform ID" toCopy={data.id}>
              ID {data.id}
            </ChipWithCopy>
          </Stack>

          <Stack direction="row" alignItems="center" gap="16px">
            {!hideActions ? (
              <ReusableTxDetailActionsContainer
                data={data}
                onEditButtonClicked={onEditButtonClicked}
                disableEditing={false}
                disableEditMessage={null}
                readOnly={readOnly}
                isManaged={isManaged}
                hideMenuDetails={true}
              />
            ) : null}
          </Stack>
        </Stack>
      </ResourceDetailHeader.TopContent>
    </ResourceDetailHeader>
  );
};
